import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputBase,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';

const NAVY = '#002244';

const sidebarSections = [
  'Getting Started',
  'Dashboard',
  'Customers',
  'Employees',
  'Scheduling',
  'Routes',
  'Route Template',
  'Work Orders',
  'Reports',
  'Map View',
  'Billing',
  'FAQ',
];

const moduleChips = [
  { label: 'Dashboard', color: '#1565C0' },
  { label: 'Customers', color: '#2E7D32' },
  { label: 'Scheduling', color: '#E65100' },
  { label: 'Routes', color: '#00838F' },
  { label: 'Reports', color: '#AD1457' },
  { label: 'Billing', color: '#4527A0' },
];

const gettingStartedSteps = [
  {
    initials: '01',
    color: '#1565C0',
    title: 'Create Your Account',
    subtitle: 'Company Admin',
    points: [
      'Sign up with your company email address',
      'Verify your email to activate the account',
      'Set your company name and basic profile',
    ],
  },
  {
    initials: '02',
    color: '#2E7D32',
    title: 'Add Your Employees',
    subtitle: 'Company Admin',
    points: [
      'Navigate to Add Employee in the sidebar',
      "Enter each technician's name, contact info, and role",
      'Assign the correct access level from day one',
    ],
  },
  {
    initials: '03',
    color: '#E65100',
    title: 'Add Your Customers',
    subtitle: 'Company Admin',
    points: [
      'Use Add Customer to enter each customer\'s details',
      'Include pool type, service frequency, and chemical preferences',
      'Accurate data makes scheduling and reporting more powerful',
    ],
  },
  {
    initials: '04',
    color: '#00838F',
    title: 'Set Up Scheduling',
    subtitle: 'Company Admin',
    points: [
      'Open Scheduling and assign service days to each customer',
      'Open Employee Scheduling and assign employees to routes',
      'Use drag and drop to arrange visits quickly',
    ],
  },
  {
    initials: '05',
    color: '#AD1457',
    title: 'Review Your Routes',
    subtitle: 'Company Admin',
    points: [
      'Go to Routes Summary for a full view by day and employee',
      'Verify stop counts and route order before dispatch',
      'Make any adjustments before technicians head out',
    ],
  },
  {
    initials: '06',
    color: '#4527A0',
    title: 'Start Logging Work',
    subtitle: 'All Technicians',
    points: [
      'Technicians create Work Orders with chemical readings',
      'Add notes to each service visit',
      'Records feed directly into the Reports page',
    ],
  },
];

const featureSections = {
  Dashboard: {
    color: '#1565C0',
    description: 'Your central hub for real-time business metrics and quick actions.',
    chips: ['Metrics', 'Charts', 'Quick Actions', 'Activity Feed'],
    sectionLabel: 'DASHBOARD FEATURES',
    topics: [
      {
        initials: 'OC',
        color: '#1565C0',
        title: 'Overview Cards',
        subtitle: 'Real-time metrics',
        points: [
          'Total active customers and employees at a glance',
          'Work orders for today — completed and pending counts',
          'Updates in real time whenever data changes',
        ],
      },
      {
        initials: 'WT',
        color: '#1976D2',
        title: 'Weekly Trend Chart',
        subtitle: 'Performance over time',
        points: [
          'Area chart of routes and tasks completed per day',
          'Navigate between weeks with left/right arrows',
          'Compare performance trends across weeks',
        ],
      },
      {
        initials: 'SD',
        color: '#1565C0',
        title: 'Service Distribution',
        subtitle: 'Visit breakdown by type',
        points: [
          'Pie chart breaking down visits by service type',
          'Covers chlorine, salt, spa, and custom types',
          'Helps identify which services are most in demand',
        ],
      },
      {
        initials: 'QA',
        color: '#1976D2',
        title: 'Quick Actions',
        subtitle: 'Shortcuts to key pages',
        points: [
          'Jump to Add Customer, Add Employee, and Scheduling',
          'Access Routes Summary and Reports instantly',
          'Saves time navigating through the sidebar',
        ],
      },
    ],
  },
  Customers: {
    color: '#2E7D32',
    description: 'Manage your customer database, work orders, and service schedules.',
    chips: ['Add Customer', 'View Customers', 'Status', 'History', 'Delete'],
    sectionLabel: 'CUSTOMER MANAGEMENT',
    topics: [
      {
        initials: 'AC',
        color: '#2E7D32',
        title: 'Adding a Customer',
        subtitle: 'Multi-step form',
        points: [
          'Fill in name, address, and service details',
          'Select pool type, chemical preferences, and billing info',
          'All required fields are marked with an asterisk',
        ],
      },
      {
        initials: 'DC',
        color: '#388E3C',
        title: 'Deleting a Customer',
        subtitle: 'Deactivate first, then delete',
        points: [
          'Click on Active Customers on the home page and locate the customer you want to remove',
          'First deactivate the customer by setting their status to Inactive',
          'Once deactivated, open the customer detail panel and click Delete to permanently remove the record',
        ],
      },
      {
        initials: 'VC',
        color: '#388E3C',
        title: 'Viewing Customers',
        subtitle: 'Searchable table',
        points: [
          'Lists all active and inactive customers',
          'Filter by name, status, or service type',
          'Click any row to expand full customer details',
        ],
      },
      {
        initials: 'CS',
        color: '#2E7D32',
        title: 'Customer Status',
        subtitle: 'Active vs. Inactive',
        points: [
          'Active customers appear in scheduling and routes',
          'Inactive customers are hidden from scheduling',
          'Historical records are always retained in the database',
        ],
      },
      {
        initials: 'SH',
        color: '#388E3C',
        title: 'Service History',
        subtitle: 'Per-customer visit records',
        points: [
          'All completed work orders are tied to the customer',
          'View chemical readings and technician notes per visit',
          'Accessible from the customer detail panel',
        ],
      },
    ],
  },
  'Work Orders': {
    color: '#5D4037',
    description: 'Track individual service visits, chemical readings, and technician notes.',
    chips: ['Create', 'Status', 'Chemical Readings', 'Notes'],
    sectionLabel: 'WORK ORDER MANAGEMENT',
    topics: [
      {
        initials: 'CR',
        color: '#6D4C41',
        title: 'Chemical Readings',
        subtitle: 'Pool data logging',
        points: [
          'Log chlorine, pH, alkalinity, and other readings',
          'Data is saved per visit for historical tracking',
          'Visible in the Reports export and customer history',
        ],
      },
      {
        initials: 'WS',
        color: '#5D4037',
        title: 'Work Order Status',
        subtitle: 'Open vs. Completed',
        points: [
          'Mark orders as Open, In Progress, or Completed',
          'Completed orders feed into the dashboard metrics',
          'Filter by status on the Work Orders page',
        ],
      },
      {
        initials: 'TN',
        color: '#6D4C41',
        title: 'Technician Notes',
        subtitle: 'Visit documentation',
        points: [
          'Add free-text notes to any work order',
          'Notes are included in PDF and Excel exports',
          'Useful for flagging issues or follow-up actions',
        ],
      },
    ],
  },
  Employees: {
    color: '#6A1B9A',
    description: 'Add, manage, and track your field technicians and office staff.',
    chips: ['Add Employee', 'View Employees', 'Roles', 'Access'],
    sectionLabel: 'EMPLOYEE MANAGEMENT',
    topics: [
      {
        initials: 'AE',
        color: '#6A1B9A',
        title: 'Adding an Employee',
        subtitle: 'Admin only',
        points: [
          'Provide name, contact information, and role',
          'Set designation: Technician, Supervisor, or Admin',
          'Employee receives login credentials automatically',
        ],
      },
      {
        initials: 'VE',
        color: '#7B1FA2',
        title: 'Viewing Employees',
        subtitle: 'Staff directory',
        points: [
          'See all staff with contact details and current role',
          'View current route assignments at a glance',
          'Search by name to find someone quickly',
        ],
      },
      {
        initials: 'RL',
        color: '#6A1B9A',
        title: 'Employee Roles',
        subtitle: 'Access control',
        points: [
          'Admins manage the entire system including billing',
          'Technicians only have access to use the mobile app',
          'All service data must be entered by technicians through work orders on the mobile app',
          'Roles can be updated at any time by an Admin',
        ],
      },
      {
        initials: 'RA',
        color: '#7B1FA2',
        title: 'Route Assignment',
        subtitle: 'Day-to-day dispatch',
        points: [
          'Employees are assigned routes via Employee Scheduling',
          'Each employee sees only their assigned stops',
          'Reassignment is instant via drag and drop',
        ],
      },
    ],
  },
  Scheduling: {
    color: '#E65100',
    description: 'Plan and manage customer service visits and employee calendars.',
    chips: ['Customer Schedule', 'Employee Schedule', 'Drag & Drop', 'Weekly View'],
    sectionLabel: 'SCHEDULING FEATURES',
    topics: [
      {
        initials: 'CS',
        color: '#E65100',
        title: 'Customer Scheduling',
        subtitle: 'Assign service days',
        points: [
          'Drag customers onto days of the week',
          'Sets their recurring visit schedule automatically',
          'Changes reflect immediately in Routes Summary',
        ],
      },
      {
        initials: 'CW',
        color: '#E65100',
        title: 'Creating a Work Order',
        subtitle: 'Per service visit',
        points: [
          'Link the work order to a customer and date',
          'Select the assigned technician',
          'Add service type and any special instructions',
        ],
      },
      {
        initials: 'ES',
        color: '#EF6C00',
        title: 'Employee Scheduling',
        subtitle: 'Technician calendar',
        points: [
          'Shows which technicians cover which days and routes',
          'Reassign routes by dragging cards between employees',
          'Calendar view makes conflicts easy to spot',
        ],
      },
      {
        initials: 'DD',
        color: '#E65100',
        title: 'Drag & Drop',
        subtitle: 'Fast rearrangement',
        points: [
          'Grab any customer or employee card to move it',
          'Drop onto the desired day column or time slot',
          'System highlights valid drop zones automatically',
        ],
      },
      {
        initials: 'WV',
        color: '#EF6C00',
        title: 'Week Navigation',
        subtitle: 'Past and future views',
        points: [
          'Use arrows to move forward or backward by week',
          'Current week is always highlighted',
          'Completed visits show a green checkmark',
        ],
      },
    ],
  },
  Routes: {
    color: '#00838F',
    description: 'Optimize and review daily service routes for your field teams.',
    chips: ['Route Summary', 'Optimization', 'Print', 'Export'],
    sectionLabel: 'ROUTE MANAGEMENT',
    topics: [
      {
        initials: 'RS',
        color: '#00838F',
        title: 'Routes Summary',
        subtitle: "Bird's-eye view",
        points: [
          'Groups customers by service day and employee',
          'Shows stop count per technician per day',
          'Quick check before dispatch each morning',
        ],
      },
      {
        initials: 'RO',
        color: '#00695C',
        title: 'Route Optimization',
        subtitle: 'Minimize drive time',
        points: [
          'Routes ordered automatically to minimize mileage',
          'Uses customer addresses for geographic sequencing',
          'Manual reordering available via drag and drop',
        ],
      },
      {
        initials: 'PE',
        color: '#00838F',
        title: 'Printing & Exporting',
        subtitle: 'Offline reference',
        points: [
          'Print any daily route directly from Routes Summary',
          'Useful for technicians who prefer a paper copy',
          'Export for offline or record-keeping purposes',
        ],
      },
      {
        initials: 'DT',
        color: '#00695C',
        title: 'Daily Tracking',
        subtitle: 'Progress monitoring',
        points: [
          'Completed stops are marked as the day progresses',
          'Monitor route completion from the dashboard',
          'See which customers are still pending for the day',
        ],
      },
    ],
  },
  'Route Template': {
    color: '#1565C0',
    description: 'Save schedule snapshots, restore routes from the Template Library, and manage company-wide and per-employee backups.',
    chips: ['Save Recovery Template', 'Template Library', 'Review & Restore', 'Scopes', 'Delete'],
    sectionLabel: 'ROUTE TEMPLATE FEATURES',
    topics: [
      {
        initials: 'ST',
        color: '#1565C0',
        title: 'Saving a Template',
        subtitle: 'Save Recovery Template button',
        points: [
          'On the Employee Scheduling page, click Save Recovery Template (visible to Owners, Managers, Co-Owners, and Admins only)',
          'Confirm the prompt, then give the snapshot a name and optional description',
          'Choose a scope of the whole company full week or a single employee\'s full week',
          'The snapshot captures the live schedule exactly as it appears at that moment — save one before making large changes so you can roll back',
        ],
      },
      {
        initials: 'SC',
        color: '#1976D2',
        title: 'Template Scopes',
        subtitle: 'What each template captures',
        points: [
          'Company Week — captures every employee\'s full week of routes; the broadest restore point for the whole company',
          'Employee Week — captures one employee\'s routes across all days of the week',
          'The scope is chosen at save time and shown as a label on each template in the Template Library',
        ],
      },
      {
        initials: 'SP',
        color: '#1565C0',
        title: 'Shared vs. Private',
        subtitle: 'Visibility control',
        points: [
          'Templates are shared with all managers and co-managers by default',
          'Uncheck the Share checkbox when saving to keep the template visible only to yourself',
          'The share setting is configured at save time',
        ],
      },
      {
        initials: 'LT',
        color: '#1976D2',
        title: 'Using a Template',
        subtitle: 'Template Library on Employee Scheduling',
        points: [
          'Open the Template Library from the Employee Scheduling page to browse all available templates with their scope, stop count, and shared/private status',
          'Day-scope templates show an Add to current schedule button — adds those stops to the currently open view without replacing anything',
          'For full recovery, click Review & Restore to see a before-and-after diff of every affected route before committing any changes',
          'You can also open Route Templates from the sidebar to preview any template\'s stop order and click Use on Scheduling Page',
        ],
      },
      {
        initials: 'RR',
        color: '#1565C0',
        title: 'Review & Restore',
        subtitle: 'Diff preview before applying',
        points: [
          'Click Review & Restore in the Template Library to preview exactly what will change before anything is applied',
          'Choose Overwrite to replace routes entirely, or Merge to add only customers missing from the current route',
          'Overwrite mode shows a warning — any customer currently scheduled but not in the template will be cancelled',
          'Review the Now vs After Restore columns per route; you can reorder stops, remove stops, or add removed stops back before confirming',
        ],
      },
      {
        initials: 'CF',
        color: '#1976D2',
        title: 'Cross-employee Conflicts',
        subtitle: 'Customer on another employee\'s route',
        points: [
          'If a customer being restored is currently on a different employee\'s route, a conflict panel appears before you confirm',
          'This week only — borrows the customer for this week; the other employee keeps them permanently and they reappear on that route next week',
          'Permanent — moves the customer to the restored route and removes them from the other employee\'s active schedule',
          'Delete route — removes the customer from this day entirely and does not add them to the restored route',
        ],
      },
      {
        initials: 'AB',
        color: '#1565C0',
        title: 'Auto Backups',
        subtitle: 'System-generated snapshots',
        points: [
          'Some templates in the library display a green Auto badge — these are snapshots the system creates automatically on a schedule',
          'Auto backups require no manual action and always reflect the company\'s full week at the time they were taken',
          'They work identically to manually saved templates and can be used with Review & Restore in the same way',
        ],
      },
      {
        initials: 'ND',
        color: '#1976D2',
        title: 'Re-applying with No Changes',
        subtitle: 'Date drift fix',
        points: [
          'If the restore preview shows zero added, removed, and reordered, the active schedule already matches the template structure',
          'This can happen when work order dates have drifted — the schedule page may show fewer customers than expected even though the routes look correct',
          'Click Re-apply Anyway to recreate all work orders with fresh dates without changing the route structure',
        ],
      },
      {
        initials: 'DT',
        color: '#1565C0',
        title: 'Deleting a Template',
        subtitle: 'Owners and managers only',
        points: [
          'Click the delete icon next to any template in the list',
          'A confirmation prompt will appear — deletion cannot be undone',
          'Only users with Owner, Manager, Co-Owner, or Admin roles can delete templates',
        ],
      },
    ],
  },
  Reports: {
    color: '#AD1457',
    description: 'Generate, filter, and export service reports in PDF or Excel format.',
    chips: ['PDF Export', 'Excel Export', 'Date Range', 'Filters'],
    sectionLabel: 'REPORTING FEATURES',
    topics: [
      {
        initials: 'GR',
        color: '#AD1457',
        title: 'Generating a Report',
        subtitle: 'Flexible date ranges',
        points: [
          'Select customer, employee, and date range filters',
          'Quick ranges: Today, This Week, This Month, This Quarter',
          'Click Search to populate the results table',
        ],
      },
      {
        initials: 'EF',
        color: '#C2185B',
        title: 'Employee Filter',
        subtitle: 'Per-technician view',
        points: [
          'Narrow results to a specific technician',
          'Useful for payroll verification and performance review',
          'Combine with date range for precise reporting',
        ],
      },
      {
        initials: 'PD',
        color: '#AD1457',
        title: 'Export to PDF',
        subtitle: 'Formatted document',
        points: [
          'Generates a branded report with company name and date range',
          'Includes a table of visits with readings and notes',
          'Ready to share with customers or for record keeping',
        ],
      },
      {
        initials: 'XL',
        color: '#C2185B',
        title: 'Export to Excel',
        subtitle: 'Spreadsheet download',
        points: [
          'Each row represents one task/visit record',
          'Includes chemical readings, timestamps, and technician info',
          'Use Column Selector to tailor the output before exporting',
        ],
      },
    ],
  },
  'Map View': {
    color: '#37474F',
    description: 'Visualize customer locations and technician routes on an interactive map.',
    chips: ['Customer Pins', 'Route Lines', 'Zoom & Pan', 'Territories'],
    sectionLabel: 'MAP FEATURES',
    topics: [
      {
        initials: 'CP',
        color: '#37474F',
        title: 'Customer Pins',
        subtitle: 'Location overview',
        points: [
          'Active customers plotted as pins on the map',
          'Click any pin to see name, address, and service day',
          'Visual check for coverage gaps or dense clusters',
        ],
      },
      {
        initials: 'RV',
        color: '#455A64',
        title: 'Route Visualization',
        subtitle: 'Per-employee routes',
        points: [
          'Switch employees to see their route drawn on the map',
          'Spot geographic gaps or overlapping territories',
          'Helps plan optimal route distribution',
        ],
      },
      {
        initials: 'ZP',
        color: '#37474F',
        title: 'Zoom & Pan',
        subtitle: 'Map navigation',
        points: [
          'Use standard map controls to zoom in or out',
          'Pan to any region by clicking and dragging',
          'Map auto-fits to show all pins on page load',
        ],
      },
      {
        initials: 'TZ',
        color: '#455A64',
        title: 'Territory Planning',
        subtitle: 'Visual distribution',
        points: [
          'Compare how customers are spread across employees',
          'Identify imbalances in workload or geography',
          'Use alongside scheduling for efficient route design',
        ],
      },
    ],
  },
  Billing: {
    color: '#4527A0',
    description: 'Manage your subscription plan and payment information.',
    chips: ['Plans', 'Trial', 'Payment Methods', 'Invoices'],
    sectionLabel: 'BILLING & SUBSCRIPTION',
    topics: [
      {
        initials: 'SP',
        color: '#4527A0',
        title: 'Subscription Plans',
        subtitle: 'Tier management',
        points: [
          'View your current plan and usage limits',
          'Upgrade or downgrade at any time',
          'Changes take effect at the next billing cycle',
        ],
      },
      {
        initials: 'TP',
        color: '#512DA8',
        title: 'Trial Period',
        subtitle: 'Free access window',
        points: [
          'New accounts receive a free trial period',
          'A banner appears when the trial is nearing expiration',
          'Full features remain accessible until the trial end date',
        ],
      },
      {
        initials: 'PM',
        color: '#4527A0',
        title: 'Payment Methods',
        subtitle: 'Secure card management',
        points: [
          'Add or update your credit card from the Billing page',
          'Payment processing is handled securely by our provider',
          'Card details are never stored on our servers',
        ],
      },
      {
        initials: 'IV',
        color: '#512DA8',
        title: 'Invoices',
        subtitle: 'Billing history',
        points: [
          'View past invoices and payment history',
          'Download PDF copies for your records',
          'Contact support for any billing discrepancies',
        ],
      },
    ],
  },
};

const faqs = [
  {
    q: 'How do I reset my password?',
    a: 'On the login page, click "Forgot Password." Enter your registered email address and you will receive a reset link within a few minutes. Follow the link to set a new password. If you do not see the email, check your spam folder.',
  },
  {
    q: 'Can I have multiple users for my company?',
    a: 'Yes. Admins can add employees through the Add Employee page. Each employee gets their own login credentials and access level based on their assigned role.',
  },
  {
    q: "Why isn't a customer appearing on the scheduling page?",
    a: "Customers must be marked Active to appear on the scheduling and routes pages. Open the customer's record in View Customers and confirm their status is set to Active.",
  },
  {
    q: 'How do I export data for a specific date range?',
    a: 'Go to the Reports page, select your customer and employee filters, set the start and end dates using the date pickers, click Search, then use the PDF or Excel export buttons.',
  },
  {
    q: 'What happens if I accidentally delete a work order?',
    a: 'Deleted work orders cannot be recovered from the UI. Contact support immediately if you need to restore a deleted record and we can attempt a database-level recovery.',
  },
  {
    q: 'How are routes ordered on the Routes Summary page?',
    a: 'Routes are sorted by service day first, then ordered geographically to minimize drive time within each day. You can manually reorder stops on the scheduling page by dragging customer cards.',
  },
  {
    q: 'Can I use the app on my phone?',
    a: 'The web application is designed for desktop use by owners and managers. Technicians do not use the web app. Technicians use the dedicated mobile app to view their routes, log service data, and check in and out. Owners and admins can also access the web app from a mobile browser.',
  },
  {
    q: 'How do I update my company information?',
    a: 'Company-level settings are managed by an Admin user. Go to My Account to update personal profile details. For company name or billing address changes, contact support.',
  },
  {
    q: 'Why is my weekly trend chart showing no data?',
    a: 'The chart pulls from completed work orders within the selected week. If no tasks were marked complete during that week, the chart will appear empty. Use the week navigation arrows to confirm you are viewing the correct date range.',
  },
  {
    q: 'How do I assign a customer to a technician\'s route?',
    a: 'Open the Employee Scheduling page, select the technician and day from the dropdowns. Drag the customer card from the unscheduled list into the technician\'s route column, then click Save Schedule.',
  },
  {
    q: 'How do I reassign a customer to a different technician?',
    a: 'Open the Employee Scheduling page, locate the customer card on the current technician\'s route, and drag it to a different technician\'s column. Confirm the reassignment, then click Save Schedule.',
  },
  {
    q: 'How do I assign a route to a different employee?',
    a: 'Open the Employee Scheduling page, find the route card you want to reassign, and drag it to the column of the new employee. The change is saved automatically.',
  },
  {
    q: 'How do I add a new customer?',
    a: 'Go to Add Customer in the sidebar. Fill in the customer\'s name, contact information, address, and pool details. Select the service frequency and any chemical preferences, then click Save. The customer will immediately appear in scheduling and the customer list.',
  },
  {
    q: 'How do I edit a customer\'s profile?',
    a: 'Open View Customers from the sidebar and find the customer. Click their row to expand the detail panel, then click Edit. Update the relevant fields and click Update to save your changes.',
  },
  {
    q: 'How do I set the route order for a technician?',
    a: 'Open the Employee Scheduling page, select the technician and day. Drag customer cards up or down within the route column to arrange the service order. Click Save Schedule to confirm.',
  },
  {
    q: 'How do I remove a customer from a route?',
    a: 'Open Employee Scheduling, locate the customer card on the technician\'s route, and click the remove (×) button on the card. Click Save Schedule to apply the change.',
  },
  {
    q: 'How do I view completed service details for a work order?',
    a: 'Go to the Routes page and find the completed task in the list. Click it to open the detail panel where you can review chemical readings, service notes, and any attached information.',
  },
  {
    q: 'How do I see all service history for a customer?',
    a: 'Open View Customers, click a customer to expand their detail panel, and scroll to the Service History section. Click any entry to view full chemical readings, technician notes, and visit details.',
  },
  {
    q: 'How do I add a new employee?',
    a: 'Go to Add Employee in the sidebar. Enter the employee\'s name, contact information, and role (Technician, Supervisor, or Admin). Save the record — the employee will receive their login credentials and can access the system based on their assigned role.',
  },
  {
    q: 'How do I view customers on the map?',
    a: 'Click Map View in the sidebar. All active customers are plotted as pins on the interactive map. Click any pin to see the customer\'s name, address, and scheduled service day. Use the employee filter to highlight a specific technician\'s route.',
  },
  {
    q: 'How do I change my password?',
    a: 'Go to My Account from the sidebar and look for the Change Password option. Enter your current password and your new password, then save. If you have forgotten your password, use the Forgot Password link on the login page instead.',
  },
  {
    q: "How do I view today's route summary?",
    a: "Go to the Routes page in the sidebar. It shows all customers grouped by service day and employee. Use the employee filter at the top to focus on a specific technician's stops for today.",
  },
];

const searchIndex = [
  ...gettingStartedSteps.map(step => ({
    type: 'card',
    section: 'Getting Started',
    title: step.title,
    subtitle: step.subtitle,
    points: step.points,
    color: step.color,
    initials: step.initials,
  })),
  ...Object.entries(featureSections).flatMap(([sectionName, section]) =>
    section.topics.map(topic => ({
      type: 'topic',
      section: sectionName,
      sectionColor: section.color,
      title: topic.title,
      subtitle: topic.subtitle,
      points: topic.points,
      color: topic.color,
      initials: topic.initials,
    }))
  ),
  ...faqs.map((faq, idx) => ({
    type: 'faq',
    section: 'FAQ',
    question: faq.q,
    answer: faq.a,
    idx,
  })),
];

function TopicCard({ initials, color, title, subtitle, points }) {
  return (
      <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: '1.5px solid #e0e7ef',
            borderRadius: '10px',
            height: '100%',
            boxSizing: 'border-box',
            transition: 'box-shadow 0.2s',
            background: '#fff',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,34,68,0.10)' },
          }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.72rem',
                fontFamily: 'Poppins, sans-serif',
                flexShrink: 0,
              }}
          >
            {initials}
          </Box>
          <Box>
            <Typography
                sx={{
                  fontWeight: 700,
                  color: NAVY,
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '0.88rem',
                  lineHeight: 1.3,
                }}
            >
              {title}
            </Typography>
            <Typography
                sx={{
                  color: '#888',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '0.75rem',
                  mb: 1,
                }}
            >
              {subtitle}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {points.map((pt, i) => (
                  <Box
                      key={i}
                      component="li"
                      sx={{
                        color: '#555',
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '0.8rem',
                        lineHeight: 1.65,
                        mb: 0.2,
                      }}
                  >
                    {pt}
                  </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
  );
}

function SectionLabel({ text }) {
  return (
      <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            fontSize: '0.7rem',
            color: '#888',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            mb: 1.5,
          }}
      >
        {text}
      </Typography>
  );
}

export default function HelpFaq() {
  const [activeSection, setActiveSection] = useState('Getting Started');
  const [search, setSearch] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [expandedTopic, setExpandedTopic] = useState(false);

  const renderSearchResults = () => {
    const q = search.toLowerCase().trim();
    const results = searchIndex.filter(item => {
      if (item.type === 'faq') {
        return item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
      }
      return (
        item.title.toLowerCase().includes(q) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
        (item.points && item.points.some(p => p.toLowerCase().includes(q)))
      );
    });

    if (results.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ color: '#888', fontFamily: 'Poppins, sans-serif', fontSize: '0.9rem' }}>
            No results found for "<strong>{search}</strong>"
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.8rem', color: '#888', mb: 2.5 }}>
          {results.length} result{results.length !== 1 ? 's' : ''} for "<strong>{search}</strong>"
        </Typography>
        {results.map((item, idx) => {
          if (item.type === 'faq') {
            return (
              <Paper
                key={idx}
                elevation={0}
                onClick={() => { setActiveSection('FAQ'); setSearch(''); }}
                sx={{ p: 2.5, border: '1.5px solid #e0e7ef', borderRadius: '10px', background: '#fff', mb: 1.5, cursor: 'pointer', '&:hover': { boxShadow: '0 4px 16px rgba(0,34,68,0.10)' } }}
              >
                <Chip label="FAQ" size="small" sx={{ background: NAVY, color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '0.68rem', fontWeight: 600, mb: 1 }} />
                <Typography sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Poppins, sans-serif', fontSize: '0.88rem', mb: 0.5 }}>
                  {item.question}
                </Typography>
                <Typography sx={{ color: '#555', fontFamily: 'Poppins, sans-serif', fontSize: '0.8rem', lineHeight: 1.65 }}>
                  {item.answer}
                </Typography>
              </Paper>
            );
          }
          return (
            <Paper
              key={idx}
              elevation={0}
              onClick={() => { setActiveSection(item.section); setSearch(''); }}
              sx={{ p: 2.5, border: '1.5px solid #e0e7ef', borderRadius: '10px', background: '#fff', mb: 1.5, cursor: 'pointer', '&:hover': { boxShadow: '0 4px 16px rgba(0,34,68,0.10)' } }}
            >
              <Chip
                label={item.section}
                size="small"
                sx={{ background: item.sectionColor || item.color, color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '0.68rem', fontWeight: 600, mb: 1 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: item.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.68rem', fontFamily: 'Poppins, sans-serif', flexShrink: 0 }}>
                  {item.initials}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Poppins, sans-serif', fontSize: '0.88rem', lineHeight: 1.3 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: '#888', fontFamily: 'Poppins, sans-serif', fontSize: '0.75rem', mb: 0.8 }}>
                    {item.subtitle}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {item.points.map((pt, i) => (
                      <Box key={i} component="li" sx={{ color: '#555', fontFamily: 'Poppins, sans-serif', fontSize: '0.8rem', lineHeight: 1.65, mb: 0.2 }}>
                        {pt}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderContent = () => {
    if (activeSection === 'Getting Started') {
      return (
          <Box>
            <Box sx={{ background: NAVY, borderRadius: '12px', p: 3, mb: 3 }}>
              <Typography
                  variant="h6"
                  sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}
              >
                Welcome to Pool Route Monitor
              </Typography>
              <Typography
                  variant="body2"
                  sx={{ color: '#aac4e0', fontFamily: 'Poppins, sans-serif', mb: 2, maxWidth: 560 }}
              >
                Pool Route Monitor is a complete field-service management platform for pool maintenance
                companies. It covers scheduling, routing, work orders, and reporting — so every team member
                sees exactly what they need to act on, nothing more.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {moduleChips.map(chip => (
                    <Chip
                        key={chip.label}
                        label={chip.label}
                        size="small"
                        sx={{
                          background: chip.color,
                          color: '#fff',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.72rem',
                        }}
                    />
                ))}
              </Box>
            </Box>

            <SectionLabel text="The 6-Step Onboarding Workflow" />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mt: 2,
              }}
            >
              {gettingStartedSteps.map(step => (
                <TopicCard key={step.title} {...step} />
              ))}
            </Box>
          </Box>
      );
    }

    if (activeSection === 'FAQ') {
      return (
          <Box>
            <Box sx={{ background: NAVY, borderRadius: '12px', p: 3, mb: 3 }}>
              <Typography
                  variant="h6"
                  sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography
                  variant="body2"
                  sx={{ color: '#aac4e0', fontFamily: 'Poppins, sans-serif' }}
              >
                Quick answers to the most common questions about Pool Route Monitor.
              </Typography>
            </Box>

            <SectionLabel text="Common Questions" />

            {faqs.map((faq, idx) => (
                <Accordion
                    key={idx}
                    expanded={expandedFaq === idx}
                    onChange={(_, isExpanded) => setExpandedFaq(isExpanded ? idx : false)}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: `1.5px solid ${expandedFaq === idx ? NAVY : '#d0dce8'}`,
                      borderRadius: '8px !important',
                      mb: 1.5,
                      '&:before': { display: 'none' },
                      transition: 'border-color 0.2s',
                    }}
                >
                  <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: NAVY }} />}
                      sx={{
                        background: expandedFaq === idx ? '#eef3f9' : '#fff',
                        borderRadius: '8px',
                        minHeight: 52,
                        '& .MuiAccordionSummary-content': { margin: '10px 0' },
                      }}
                  >
                    <Typography
                        sx={{
                          fontWeight: 600,
                          color: NAVY,
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '0.88rem',
                        }}
                    >
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ background: '#fff', px: 2.5, pb: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: '#444', fontFamily: 'Poppins, sans-serif', lineHeight: 1.7 }}
                    >
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
            ))}
          </Box>
      );
    }

    const section = featureSections[activeSection];
    if (!section) return null;

    return (
        <Box>
          {/* Plain text header */}
          <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}
          >
            {activeSection}
          </Typography>
          <Typography
              variant="body2"
              sx={{ color: '#666', fontFamily: 'Poppins, sans-serif', mb: 3 }}
          >
            {section.description}
          </Typography>

          {/* Accordion topic items */}
          {section.topics.map((topic, idx) => (
              <Accordion
                  key={idx}
                  expanded={expandedTopic === idx}
                  onChange={(_, isExpanded) => setExpandedTopic(isExpanded ? idx : false)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1.5px solid ${expandedTopic === idx ? '#c5d4e8' : '#e0e7ef'}`,
                    borderRadius: '8px !important',
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    background: '#fff',
                  }}
              >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#888', fontSize: '1.1rem' }} />}
                    sx={{
                      minHeight: 52,
                      px: 2.5,
                      '& .MuiAccordionSummary-content': { margin: '10px 0', alignItems: 'center', gap: 1.5 },
                    }}
                >
                  <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: topic.color,
                        flexShrink: 0,
                      }}
                  />
                  <Typography
                      sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Poppins, sans-serif', fontSize: '0.88rem' }}
                  >
                    {topic.title}
                  </Typography>
                  <Typography
                      sx={{ color: '#999', fontFamily: 'Poppins, sans-serif', fontSize: '0.78rem', ml: 0.5 }}
                  >
                    — {topic.subtitle}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2, pt: 0 }}>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {topic.points.map((pt, i) => (
                        <Box
                            key={i}
                            component="li"
                            sx={{ color: '#555', fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', lineHeight: 1.7 }}
                        >
                          {pt}
                        </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
          ))}

          {/* Related modules */}
          <Box sx={{ mt: 4 }}>
            <SectionLabel text="Related Modules" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {section.chips.map(chip => (
                  <Chip
                      key={chip}
                      label={`${chip} ›`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#c5d4e8',
                        color: NAVY,
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: '6px',
                        '&:hover': { background: '#eef3f9', cursor: 'pointer' },
                      }}
                  />
              ))}
            </Box>
          </Box>
        </Box>
    );
  };

  return (
      <Box
          sx={{
            minHeight: '100vh',
            background: '#f5f7fa',
            fontFamily: 'Poppins, sans-serif',
            pt: '4.5rem',
          }}
      >
        {/* Page Header */}
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 2 }}>
          <Typography
              variant="caption"
              sx={{ color: '#aaa', fontFamily: 'Poppins, sans-serif', fontSize: '0.75rem' }}
          >
            Find self-service tutorial material here
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <HelpOutlineIcon sx={{ color: NAVY, fontSize: '2rem' }} />
            <Box>
              <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: NAVY,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: { xs: '1.5rem', md: '1.9rem' },
                    lineHeight: 1.2,
                  }}
              >
                Help & FAQ
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Poppins, sans-serif' }}>
                Answers to common questions about every module in Pool Route Monitor.
              </Typography>
            </Box>
          </Box>

          {/* Search */}
          <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                px: 2,
                py: 1,
                border: '1.5px solid #d0dce8',
                borderRadius: '8px',
                background: '#fff',
                maxWidth: 420,
              }}
          >
            <SearchIcon sx={{ color: '#aaa', fontSize: '1.15rem' }} />
            <InputBase
                placeholder="Search sections..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.88rem', flex: 1 }}
            />
          </Paper>
        </Box>

        <Divider sx={{ mx: { xs: 2, md: 4 } }} />

        {/* Body: Sidebar + Content */}
        <Box
            sx={{
              display: 'flex',
              px: { xs: 2, md: 4 },
              pt: 3,
              pb: 5,
              alignItems: 'flex-start',
              gap: 3,
            }}
        >
          {/* Left Sidebar */}
          <Box
              sx={{
                width: 185,
                flexShrink: 0,
                position: 'sticky',
                top: '5.5rem',
              }}
          >
            {sidebarSections.map(section => (
                <Box
                    key={section}
                    onClick={() => { setActiveSection(section); setExpandedTopic(false); }}
                    sx={{
                      px: 2,
                      py: 0.9,
                      mb: 0.3,
                      borderRadius: '7px',
                      cursor: 'pointer',
                      borderLeft:
                          activeSection === section
                              ? `3px solid ${NAVY}`
                              : '3px solid transparent',
                      background: activeSection === section ? '#eef3f9' : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': { background: '#eef3f9' },
                    }}
                >
                  <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: activeSection === section ? 700 : 400,
                        color: activeSection === section ? NAVY : '#555',
                        fontSize: '0.85rem',
                      }}
                  >
                    {section}
                  </Typography>
                </Box>
            ))}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right Content Panel */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {search.trim() ? renderSearchResults() : renderContent()}
          </Box>
        </Box>
      </Box>
  );
}
